import { useQuery } from '@apollo/client';
import {
  Button,
  FormLabel,
  VStack,
  Text,
  Divider,
  Skeleton,
  Box,
  HStack,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { FieldArray, Form, Formik } from 'formik';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { mixed, object } from 'yup';

import {
  consts as commonConsts,
  url as commonUrl,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { BankAccountWithInstitutionDesignationAndOwnerGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  CurrencyInputFormField,
  DateTimeInputFormField,
} from './CustomFormInputs';
import { GET_BANK_ACCOUNTS_BY_CREDIT_FACILITY_ID } from 'packages/bankAccounts/requests/bankAccounts.gql';
import {
  GetBankAccountsByCreditFacilityIdQueryGQLType,
  GetBankAccountsByCreditFacilityIdQueryVariablesGQLType,
} from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { UploadScreenshotFormField } from 'packages/common-frontend/components/UploadScreenshotFormField';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { isAdminDashboard } from 'packages/common-frontend/utils';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type Props = {
  organizationId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;

  // Optional file version against which cash balance will be uploaded.
  // When not provided, BE will find the correct version based on the reporting date.
  // BBPL Monitor provides this value. legacy Manual Data Tape Uploader does not.
  fileVersion?: number;
  accept: any;
  onClose: () => void;
  onLoading: (creditFacilityId: string) => void;
  onSubmit: (artifactInstanceId?: string) => Promise<void>;
  onSuccess: (creditFacilityId: string) => void;
  onError: (message: string) => void;
};

const postForm = ({
  creditFacilityId,
  reportingDate,
  formValues,
  fileVersion,
  token,
}: {
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  formValues: any;
  fileVersion?: number;
  token: string;
}) => {
  const url = commonUrl.getCashBalanceFormUploadUrl({
    admin: isAdminDashboard(),
  });

  const formData = new FormData();
  formData.append('creditFacilityId', creditFacilityId);
  formData.append('reportingDate', reportingDate);

  if (typeof fileVersion === 'number') {
    // its only deliverables code-path that does not provide version.
    // update those and make this required parameter.
    formData.append('fileVersion', fileVersion.toString());
  }

  type AccountBalance = {
    id: string;
    balance: string;
    retrievedAt: Date;
    conversionRate: number;
  };

  const accountBalances: AccountBalance[] = [];

  _.forEach(formValues.accounts, (account, id) => {
    if (account.balance === null) {
      throw new Error('Account balance must be present.');
    }
    const formattedRetrievedAt = DateTime.fromFormat(
      account.retrievedAt,
      "yyyy-MM-dd'T'HH:mm",
    ).toJSDate();
    accountBalances.push({
      id,
      conversionRate: 1, // default conversion rate
      ...account,
      retrievedAt: formattedRetrievedAt,
    });
  });

  formData.append('accountBalances', JSON.stringify(accountBalances));

  if (formValues.file) {
    _.forEach(formValues.file, (file, key) => {
      formData.append(`screenshot-${key}`, file);
    });
  }

  return axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      authorization: `Bearer ${token}`,
    },
  });
};

const formattedAccountName = (
  bankAccount: BankAccountWithInstitutionDesignationAndOwnerGQLType,
): string => {
  const accountName = bankAccount.name;
  const accountNumberLastFour = bankAccount.accountNumber.slice(-4);
  return `${bankAccount.institutionName}: ${accountName} (...${accountNumberLastFour})`;
};

export const UploadCashBalanceForm = ({
  organizationId,
  creditFacilityId,
  reportingDate,
  fileVersion,
  accept,
  onClose,
  onLoading,
  onSubmit,
  onSuccess,
  onError,
}: Props) => {
  const { accessToken } = useUserAuthOrThrow();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const { loading: bankAccountsLoading, data } = useQuery<
    GetBankAccountsByCreditFacilityIdQueryGQLType,
    GetBankAccountsByCreditFacilityIdQueryVariablesGQLType
  >(GET_BANK_ACCOUNTS_BY_CREDIT_FACILITY_ID, {
    variables: { organizationId, creditFacilityId },
  });

  if (bankAccountsLoading || !data) {
    return <Skeleton />;
  }

  const bankAccounts = _.filter(
    data.bankAccountsWithDesignationAndOwner,
    (bankAccount) => {
      return _.includes(
        commonConsts.cashBalanceFeedContributionAccountTypes,
        bankAccount.accountType,
      );
    },
  );

  // Retrieving first segment of ISO string including minutes:
  // yyyy-MM-dd'T'HH:mm
  const defaultRetrievedAt = DateTime.now().toISO().slice(0, 16);

  const initialValues = {
    file: null,
    accounts: bankAccounts.map((bankAccount) => {
      return {
        id: bankAccount.id,
        balance: null,
        retrievedAt: defaultRetrievedAt,
      };
    }),
  };

  return (
    <Formik
      onSubmit={async (values) => {
        try {
          if (values) {
            onLoading(creditFacilityId);
            setLoading(true);
            const { data: uploadData } = await postForm({
              creditFacilityId,
              reportingDate,
              formValues: values,
              token: accessToken.raw,
              fileVersion,
            });
            await onSubmit(uploadData.artifactInstanceId);
            toast({
              ...successToastConfig,
              title: 'Successfully uploaded cash balances',
            });
            onSuccess(creditFacilityId);
          }
        } catch (err) {
          const message =
            err instanceof Error
              ? err.message
              : 'Error uploading cash balances';
          onError(message);
          if (!toast.isActive(errorToastId)) {
            toast({ ...errorToastConfig, title: message });
          }
          setLoading(false);
          throw err;
        }
        setLoading(false);
        onClose();
      }}
      validationSchema={object().shape({
        file: mixed(),
      })}
      initialValues={initialValues}
      validateOnChange={false}
    >
      {({ handleSubmit, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <VStack spacing="10px">
            <FieldArray
              name="accounts"
              render={() => {
                return bankAccounts.map((bankAccount, idx) => (
                  <VStack key={bankAccount.id} w="full" spacing="12px">
                    {idx !== 0 && (
                      <Divider backgroundColor="gray.200" my="10px" />
                    )}
                    <Text alignSelf="baseline" fontWeight="bold">
                      {formattedAccountName(bankAccount)}
                    </Text>
                    <Box width="45%" alignSelf="flex-start">
                      <CurrencyInputFormField
                        label="Cash Balance"
                        name={`accounts[${idx}].balance`}
                      />
                    </Box>
                    <FormLabel alignSelf="baseline">
                      Screenshot of Cash Balance (Optional)
                    </FormLabel>
                    <UploadScreenshotFormField
                      isDisabled={loading}
                      setFieldValue={(value: FileWithPath) =>
                        setFieldValue(`file[${bankAccount.id}]`, value)
                      }
                      accept={accept}
                    />
                    <Box width="45%" alignSelf="flex-start">
                      <DateTimeInputFormField
                        label="Balance Retrieved At:"
                        name={`accounts[${idx}].retrievedAt`}
                        type="datetime-local"
                        defaultValue={defaultRetrievedAt}
                      />
                    </Box>
                  </VStack>
                ));
              }}
            />
            <Box py="12px" alignSelf="flex-end">
              <HStack spacing="25px">
                <Button
                  variant="outline"
                  colorScheme="aurora"
                  onClick={onClose}
                  px="20px"
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  colorScheme="aurora"
                  isLoading={loading}
                  type="submit"
                  px="20px"
                >
                  Submit
                </Button>
              </HStack>
            </Box>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};
